import {
  LANDMARKS,
  clamp,
  lerp,
  NOSE_FEATURE_INDICES,
  CHEEK_FEATURE_INDICES,
  JAW_EAR_FEATURE_INDICES
} from './helpers'

import {
  EYE_LAYER_POLYGONS,
  FACE_MESH_CONTOUR_PATHS,
  IRIS_INDICES
} from '../avatarInspector/featureIndices'

// Utility: create an array of canonical points
export const createPointArray = (count) =>
  Array.from({ length: count }, () => ({ x: 0, y: 0, z: 0 }))

export const computeFaceMetrics = (landmarks, width, height) => {
  if (!landmarks || !landmarks.length) return null
  const leftEye = landmarks[LANDMARKS.leftEye]
  const rightEye = landmarks[LANDMARKS.rightEye]
  const nose = landmarks[LANDMARKS.noseTip]
  const forehead = landmarks[LANDMARKS.forehead]
  const chin = landmarks[LANDMARKS.chin]
  if (!leftEye || !rightEye || !nose || !forehead || !chin) return null

  const eyeMidX = (leftEye.x + rightEye.x) / 2
  const eyeMidY = (leftEye.y + rightEye.y) / 2
  const centerNX = eyeMidX * 0.5 + nose.x * 0.5
  const centerNY = eyeMidY * 0.5 + nose.y * 0.5
  const centerX = centerNX * width
  const centerY = centerNY * height

  const eyeDx = (rightEye.x - leftEye.x) * width
  const eyeDy = (rightEye.y - leftEye.y) * height
  const eyeDist = Math.hypot(eyeDx, eyeDy)

  // Use nose-to-forehead as a stable vertical metric that doesn't change when mouth opens
  const coreHeight = Math.abs(nose.y - forehead.y) * height
  // Scale coreHeight to roughly approximate a full face height
  const stableFaceH = coreHeight * 1.8

  return {
    centerX,
    centerY,
    rotation: Math.atan2(eyeDy, eyeDx),
    faceW: Math.max(40, eyeDist * 2.4),
    faceH: Math.max(40, stableFaceH)
  }
}

export const toCanonical = (landmarks, metrics, width, height, count = landmarks.length) => {
  if (!metrics) return null
  const cos = Math.cos(-metrics.rotation)
  const sin = Math.sin(-metrics.rotation)
  const points = createPointArray(count)
  for (let i = 0; i < count; i += 1) {
    const lm = landmarks[i]
    if (!lm) continue
    const lx = lm.x * width
    const ly = lm.y * height
    const dx = lx - metrics.centerX
    const dy = ly - metrics.centerY
    const rx = dx * cos - dy * sin
    const ry = dx * sin + dy * cos
    points[i].x = rx / metrics.faceW
    points[i].y = ry / metrics.faceH
    points[i].z = (lm.z || 0) * (width / Math.max(1, metrics.faceW))
  }
  return points
}

export const canonicalToCanvas = (canonicalPoints, metrics, out = null) => {
  if (!canonicalPoints || !metrics) return null
  const cos = Math.cos(metrics.rotation)
  const sin = Math.sin(metrics.rotation)
  const count = canonicalPoints.length
  const points = out && out.length === count ? out : createPointArray(count)
  for (let i = 0; i < count; i += 1) {
    const cp = canonicalPoints[i]
    const dx = cp.x * metrics.faceW
    const dy = cp.y * metrics.faceH
    const rx = dx * cos - dy * sin
    const ry = dx * sin + dy * cos
    points[i].x = metrics.centerX + rx
    points[i].y = metrics.centerY + ry
  }
  return points
}

export const applyRegionScale = (points, center, radiusX, radiusY, scaleY) => {
  if (!points || !center || !Number.isFinite(scaleY)) return
  const rx = Math.max(1e-4, radiusX || 0)
  const ry = Math.max(1e-4, radiusY || 0)
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    const dx = p.x - center.x
    const dy = p.y - center.y
    if (Math.abs(dx) <= rx && Math.abs(dy) <= ry) {
      p.y = center.y + dy * scaleY
    }
  }
}

export const applyRegionScaleAsymY = (
  points,
  center,
  radiusX,
  radiusY,
  scaleUpperY,
  scaleLowerY
) => {
  if (!points || !center) return
  const rx = Math.max(1e-4, radiusX || 0)
  const ry = Math.max(1e-4, radiusY || 0)
  const up = Number.isFinite(scaleUpperY) ? scaleUpperY : 1
  const lo = Number.isFinite(scaleLowerY) ? scaleLowerY : 1
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    const dx = p.x - center.x
    const dy = p.y - center.y
    if (Math.abs(dx) <= rx && Math.abs(dy) <= ry) {
      const s = dy < 0 ? up : lo
      p.y = center.y + dy * s
    }
  }
}

export const applyRegionScaleXY = (points, center, radiusX, radiusY, scaleX, scaleY) => {
  if (!points || !center) return
  const rx = Math.max(1e-4, radiusX || 0)
  const ry = Math.max(1e-4, radiusY || 0)
  const sx = Number.isFinite(Number(scaleX)) ? Number(scaleX) : 1
  const sy = Number.isFinite(Number(scaleY)) ? Number(scaleY) : 1
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    const dx = p.x - center.x
    const dy = p.y - center.y
    if (Math.abs(dx) <= rx && Math.abs(dy) <= ry) {
      p.x = center.x + dx * sx
      p.y = center.y + dy * sy
    }
  }
}

export const pickCanonicalPoint = (points, idx) => {
  if (!points || idx < 0 || idx >= points.length) return null
  return points[idx]
}

export const getEyeCenter = (points, outerIdx, innerIdx, upperIdx, lowerIdx) => {
  const outer = pickCanonicalPoint(points, outerIdx)
  const inner = pickCanonicalPoint(points, innerIdx)
  const upper = pickCanonicalPoint(points, upperIdx)
  const lower = pickCanonicalPoint(points, lowerIdx)
  if (!outer || !inner || !upper || !lower) return null
  return {
    x: (outer.x + inner.x) * 0.5,
    y: (upper.y + lower.y) * 0.5,
    width: Math.max(1e-4, Math.abs(inner.x - outer.x)),
    height: Math.max(1e-4, Math.abs(lower.y - upper.y))
  }
}

export const avgY = (points, indices) => {
  if (!points || !indices?.length) return null
  let sum = 0
  let count = 0
  for (let i = 0; i < indices.length; i += 1) {
    const p = points[indices[i]]
    if (!p) continue
    sum += p.y
    count += 1
  }
  return count ? sum / count : null
}

export const translateIndicesY = (points, indices, dy) => {
  if (!points || !indices?.length || !Number.isFinite(dy)) return
  for (let i = 0; i < indices.length; i += 1) {
    const idx = indices[i]
    if (!points[idx]) continue
    points[idx].y += dy
  }
}

export const translateIndicesWeighted = (points, indices, dx, dy, center, sigma = 0.18) => {
  if (!points || !indices?.length || !center) return
  const s2 = Math.max(1e-4, sigma * sigma)
  for (let i = 0; i < indices.length; i += 1) {
    const idx = indices[i]
    const p = points[idx]
    if (!p) continue
    const d2 = (p.x - center.x) * (p.x - center.x) + (p.y - center.y) * (p.y - center.y)
    const w = Math.exp(-d2 / (2 * s2))
    p.x += dx * w
    p.y += dy * w
  }
}

export const selectNearestIndices = (points, indices, center, count = 4) => {
  if (!points || !indices?.length || !center) return []
  return indices
    .map((idx) => ({ idx, p: points[idx] }))
    .filter((o) => !!o.p)
    .sort(
      (a, b) =>
        Math.hypot(a.p.x - center.x, a.p.y - center.y) -
        Math.hypot(b.p.x - center.x, b.p.y - center.y)
    )
    .slice(0, Math.max(1, count))
    .map((o) => o.idx)
}

export const readIrisCenter = (points, centerIdx, ring) => {
  const center = pickCanonicalPoint(points, centerIdx)
  if (center) return center
  if (!points || !ring?.length) return null
  let sx = 0
  let sy = 0
  let count = 0
  for (let i = 0; i < ring.length; i += 1) {
    const p = pickCanonicalPoint(points, ring[i])
    if (!p) continue
    sx += p.x
    sy += p.y
    count += 1
  }
  if (!count) return null
  return { x: sx / count, y: sy / count }
}

export const computeEyeGaze = (livePoints, rig) => {
  const fallback = {
    left: { x: 0, y: 0 },
    right: { x: 0, y: 0 }
  }
  if (!livePoints?.length || !rig?.canonical?.length) return fallback

  const liveLeftEye = getEyeCenter(
    livePoints,
    LANDMARKS.leftEye,
    LANDMARKS.leftEyeInner,
    LANDMARKS.leftEyeUpper,
    LANDMARKS.leftEyeLower
  )
  const liveRightEye = getEyeCenter(
    livePoints,
    LANDMARKS.rightEye,
    LANDMARKS.rightEyeInner,
    LANDMARKS.rightEyeUpper,
    LANDMARKS.rightEyeLower
  )
  const rigLeftEye = getEyeCenter(
    rig.canonical,
    LANDMARKS.leftEye,
    LANDMARKS.leftEyeInner,
    LANDMARKS.leftEyeUpper,
    LANDMARKS.leftEyeLower
  )
  const rigRightEye = getEyeCenter(
    rig.canonical,
    LANDMARKS.rightEye,
    LANDMARKS.rightEyeInner,
    LANDMARKS.rightEyeUpper,
    LANDMARKS.rightEyeLower
  )
  if (!liveLeftEye || !liveRightEye || !rigLeftEye || !rigRightEye) return fallback

  const liveLeftIris = readIrisCenter(livePoints, IRIS_INDICES.leftCenter, IRIS_INDICES.leftRing)
  const liveRightIris = readIrisCenter(livePoints, IRIS_INDICES.rightCenter, IRIS_INDICES.rightRing)
  const rigLeftIris = readIrisCenter(rig.canonical, IRIS_INDICES.leftCenter, IRIS_INDICES.leftRing)
  const rigRightIris = readIrisCenter(
    rig.canonical,
    IRIS_INDICES.rightCenter,
    IRIS_INDICES.rightRing
  )
  if (!liveLeftIris || !liveRightIris || !rigLeftIris || !rigRightIris) return fallback

  const normDelta = (liveIris, liveEye, rigIris, rigEye) => {
    const liveDx = (liveIris.x - liveEye.x) / Math.max(1e-4, liveEye.width * 0.5)
    const liveDy = (liveIris.y - liveEye.y) / Math.max(1e-4, liveEye.height * 0.5)
    const rigDx = (rigIris.x - rigEye.x) / Math.max(1e-4, rigEye.width * 0.5)
    const rigDy = (rigIris.y - rigEye.y) / Math.max(1e-4, rigEye.height * 0.5)
    return {
      x: clamp(liveDx - rigDx, -1, 1),
      y: clamp(liveDy - rigDy, -1, 1)
    }
  }

  return {
    left: normDelta(liveLeftIris, liveLeftEye, rigLeftIris, rigLeftEye),
    right: normDelta(liveRightIris, liveRightEye, rigRightIris, rigRightEye)
  }
}

export const boostWeightsNear = (weights, points, center, radiusX, radiusY, strength) => {
  if (!weights || !points || !center) return
  const rx = Math.max(0.01, radiusX)
  const ry = Math.max(0.01, radiusY)
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    const nx = (p.x - center.x) / rx
    const ny = (p.y - center.y) / ry
    const dist = Math.hypot(nx, ny)
    if (dist > 1.6) continue
    const influence = clamp(1 - dist / 1.6, 0, 1)
    weights[i] = Math.max(weights[i], influence * strength)
  }
}

export const buildExpressionWeights = (canonicalPoints, rig) => {
  const count = canonicalPoints?.length || 0
  const weights = new Float32Array(count)
  const nose = pickCanonicalPoint(canonicalPoints, LANDMARKS.noseTip) || { x: 0, y: 0 }
  for (let i = 0; i < count; i += 1) {
    const p = canonicalPoints[i]
    if (!p) {
      weights[i] = 0
      continue
    }
    const nx = (p.x - nose.x) / 0.72
    const ny = (p.y - nose.y) / 0.86
    const d = Math.hypot(nx, ny)
    weights[i] = d > 1.05 ? 0.004 : 0.014
  }
  if (!count || !rig) return weights

  if (rig.mouthCenter) {
    boostWeightsNear(
      weights,
      canonicalPoints,
      rig.mouthCenter,
      Math.max(0.03, rig.mouthRadiusX * 2.3),
      Math.max(0.03, rig.mouthRadiusY * 2.6),
      1.0
    )
  }
  if (rig.leftEyeCenter) {
    boostWeightsNear(
      weights,
      canonicalPoints,
      rig.leftEyeCenter,
      Math.max(0.02, rig.leftEyeRadiusX * 3),
      Math.max(0.02, rig.leftEyeRadiusY * 4),
      0.78
    )
    boostWeightsNear(
      weights,
      canonicalPoints,
      { x: rig.leftEyeCenter.x, y: rig.leftEyeCenter.y - rig.leftEyeRadiusY * 3.2 },
      Math.max(0.02, rig.leftEyeRadiusX * 3),
      Math.max(0.02, rig.leftEyeRadiusY * 2.4),
      0.62
    )
  }
  if (rig.rightEyeCenter) {
    boostWeightsNear(
      weights,
      canonicalPoints,
      rig.rightEyeCenter,
      Math.max(0.02, rig.rightEyeRadiusX * 3),
      Math.max(0.02, rig.rightEyeRadiusY * 4),
      0.78
    )
    boostWeightsNear(
      weights,
      canonicalPoints,
      { x: rig.rightEyeCenter.x, y: rig.rightEyeCenter.y - rig.rightEyeRadiusY * 3.2 },
      Math.max(0.02, rig.rightEyeRadiusX * 3),
      Math.max(0.02, rig.rightEyeRadiusY * 2.4),
      0.62
    )
  }

  if (nose) {
    boostWeightsNear(weights, canonicalPoints, nose, 0.09, 0.14, 0.38)
  }
  const chin = pickCanonicalPoint(canonicalPoints, LANDMARKS.chin)
  if (chin) {
    boostWeightsNear(weights, canonicalPoints, chin, 0.2, 0.14, 0.3)
  }

  for (let i = 0; i < count; i += 1) {
    weights[i] = clamp(weights[i], 0, 1)
  }
  return weights
}

export const buildFeatureWrapProfile = (canonicalPoints, rig) => {
  const count = canonicalPoints?.length || 0
  const wrapWeights = new Float32Array(count)
  const maxDelta = new Float32Array(count)
  const setRegion = (indices, weight, delta) => {
    if (!indices?.length) return
    for (let i = 0; i < indices.length; i += 1) {
      const idx = indices[i]
      if (idx < 0 || idx >= count) continue
      wrapWeights[idx] = Math.max(wrapWeights[idx], weight)
      maxDelta[idx] = Math.max(maxDelta[idx], delta)
    }
  }

  // Base retention keeps stylization but allows adaptive fit.
  for (let i = 0; i < count; i += 1) {
    wrapWeights[i] = 0.12
    maxDelta[i] = 0.05
  }

  const LIP_FEATURE_INDICES = [
    ...FACE_MESH_CONTOUR_PATHS.lipsOuter,
    ...FACE_MESH_CONTOUR_PATHS.lipsInner
  ]
  const EYE_FEATURE_INDICES = [
    ...EYE_LAYER_POLYGONS.left,
    ...EYE_LAYER_POLYGONS.right,
    ...IRIS_INDICES.leftRing,
    ...IRIS_INDICES.rightRing,
    IRIS_INDICES.leftCenter,
    IRIS_INDICES.rightCenter
  ]
  const BROW_FEATURE_INDICES = [
    ...FACE_MESH_CONTOUR_PATHS.leftBrow,
    ...FACE_MESH_CONTOUR_PATHS.rightBrow
  ]

  setRegion(NOSE_FEATURE_INDICES, 0.92, 0.065)
  // Restore full weight so the chin properly follows the physical Jaw Drop
  setRegion(LIP_FEATURE_INDICES, 0.85, 0.08)
  // Slightly stronger eye region to better carry expression motion
  setRegion(EYE_FEATURE_INDICES, 0.88, 0.078)
  // Stronger brow influence and higher allowed displacement to avoid "brows under forehead"
  setRegion(BROW_FEATURE_INDICES, 0.92, 0.11)
  setRegion(CHEEK_FEATURE_INDICES, 0.6, 0.1)
  // Restore jaw boundary weight so it moves dynamically
  setRegion(JAW_EAR_FEATURE_INDICES, 0.8, 0.1)

  const nose = pickCanonicalPoint(canonicalPoints, LANDMARKS.noseTip)
  if (nose) {
    boostWeightsNear(wrapWeights, canonicalPoints, nose, 0.22, 0.26, 0.4)
  }
  if (rig?.mouthCenter) {
    boostWeightsNear(
      wrapWeights,
      canonicalPoints,
      rig.mouthCenter,
      Math.max(0.04, rig.mouthRadiusX * 3.2),
      Math.max(0.04, rig.mouthRadiusY * 3.1),
      0.42
    )
  }
  if (rig?.leftEyeCenter) {
    // Core peri-orbital boost
    boostWeightsNear(
      wrapWeights,
      canonicalPoints,
      rig.leftEyeCenter,
      Math.max(0.035, rig.leftEyeRadiusX * 4.4),
      Math.max(0.035, rig.leftEyeRadiusY * 5.2),
      0.42
    )
    // Forehead-side lift just above the eyelid to support eyebrow raises
    boostWeightsNear(
      wrapWeights,
      canonicalPoints,
      { x: rig.leftEyeCenter.x, y: rig.leftEyeCenter.y - rig.leftEyeRadiusY * 3.2 },
      Math.max(0.03, rig.leftEyeRadiusX * 3.2),
      Math.max(0.03, rig.leftEyeRadiusY * 2.8),
      0.85
    )
  }
  if (rig?.rightEyeCenter) {
    boostWeightsNear(
      wrapWeights,
      canonicalPoints,
      rig.rightEyeCenter,
      Math.max(0.035, rig.rightEyeRadiusX * 4.4),
      Math.max(0.035, rig.rightEyeRadiusY * 5.2),
      0.42
    )
    boostWeightsNear(
      wrapWeights,
      canonicalPoints,
      { x: rig.rightEyeCenter.x, y: rig.rightEyeCenter.y - rig.rightEyeRadiusY * 3.2 },
      Math.max(0.03, rig.rightEyeRadiusX * 3.2),
      Math.max(0.03, rig.rightEyeRadiusY * 2.8),
      0.85
    )
  }

  for (let i = 0; i < count; i += 1) {
    wrapWeights[i] = clamp(wrapWeights[i], 0.08, 1)
    // Permit a bit more displacement to allow upper face to lift cleanly with brows
    maxDelta[i] = clamp(maxDelta[i], 0.035, 0.16)
  }

  return {
    wrapWeights,
    maxDelta
  }
}

export const applyFeatureAwareWrap = ({
  dstCanonical,
  liveCanonicalPoints,
  profile,
  fitStrength = 0.62,
  geometryBlend = 0.72
}) => {
  if (!dstCanonical?.length || !liveCanonicalPoints?.length || !profile) return
  const count = Math.min(
    dstCanonical.length,
    liveCanonicalPoints.length,
    profile.wrapWeights?.length || 0,
    profile.maxDelta?.length || 0
  )
  if (!count) return

  const leftEyeZ = liveCanonicalPoints[LANDMARKS.leftEye]?.z || 0
  const rightEyeZ = liveCanonicalPoints[LANDMARKS.rightEye]?.z || 0
  const yawAmount = clamp(Math.abs(leftEyeZ - rightEyeZ) * 8.5, 0, 1)
  const adaptiveStrength = clamp(
    (Number(fitStrength) || 0) * 0.72 + (Number(geometryBlend) || 0) * 0.28,
    0,
    1
  )
  const sideLift = 1 + yawAmount * 0.32

  for (let i = 0; i < count; i += 1) {
    const dst = dstCanonical[i]
    const live = liveCanonicalPoints[i]
    if (!dst || !live) continue
    const regionWeight = profile.wrapWeights[i] || 0.12
    const localBlend = clamp((0.2 + regionWeight * 0.9) * adaptiveStrength, 0, 1)
    const localMax = (profile.maxDelta[i] || 0.06) * sideLift
    const dx = clamp(live.x - dst.x, -localMax, localMax)
    const dy = clamp(live.y - dst.y, -localMax, localMax)
    const dz = clamp((live.z || 0) - (dst.z || 0), -localMax * 0.85, localMax * 0.85)
    dst.x += dx * localBlend
    dst.y += dy * localBlend
    dst.z = (dst.z || 0) + dz * localBlend * 0.85
  }
}

export const decimateTriangles = (triangles, stride = 1) => {
  if (!triangles?.length || stride <= 1) return triangles || []
  const out = []
  for (let i = 0, tri = 0; i < triangles.length; i += 3, tri += 1) {
    if (tri % stride === 0) {
      out.push(triangles[i], triangles[i + 1], triangles[i + 2])
    }
  }
  return out.length ? out : triangles
}

export const stripMouthTriangles = (triangles, innerLipIndices) => {
  if (!triangles || !innerLipIndices?.length) return triangles || []
  const innerSet = new Set(innerLipIndices)
  const out = []
  for (let i = 0; i < triangles.length; i += 3) {
    // If all three vertices of a triangle belong to the inner lip ring,
    // this triangle spans across the mouth opening. Delete it to create a hole.
    if (
      innerSet.has(triangles[i]) &&
      innerSet.has(triangles[i + 1]) &&
      innerSet.has(triangles[i + 2])
    ) {
      continue
    }
    out.push(triangles[i], triangles[i + 1], triangles[i + 2])
  }
  return out
}

export const computeFacialSignals = (livePoints, neutralPoints) => {
  const fallback = { jawOpen: 0, blinkLeft: 0, blinkRight: 0 }
  if (!livePoints?.length) return fallback

  const liveMouthUpper = pickCanonicalPoint(livePoints, LANDMARKS.mouthUpper)
  const liveMouthLower = pickCanonicalPoint(livePoints, LANDMARKS.mouthLower)
  const liveMouthLeft = pickCanonicalPoint(livePoints, LANDMARKS.mouthLeft)
  const liveMouthRight = pickCanonicalPoint(livePoints, LANDMARKS.mouthRight)
  const liveLEyeUpper = pickCanonicalPoint(livePoints, LANDMARKS.leftEyeUpper)
  const liveLEyeLower = pickCanonicalPoint(livePoints, LANDMARKS.leftEyeLower)
  const liveREyeUpper = pickCanonicalPoint(livePoints, LANDMARKS.rightEyeUpper)
  const liveREyeLower = pickCanonicalPoint(livePoints, LANDMARKS.rightEyeLower)
  if (
    !liveMouthUpper ||
    !liveMouthLower ||
    !liveMouthLeft ||
    !liveMouthRight ||
    !liveLEyeUpper ||
    !liveLEyeLower ||
    !liveREyeUpper ||
    !liveREyeLower
  ) {
    return fallback
  }

  const liveMouthOpen = Math.abs(liveMouthLower.y - liveMouthUpper.y)
  const liveMouthWidth = Math.max(1e-4, Math.abs(liveMouthRight.x - liveMouthLeft.x))
  const liveLeftEyeOpen = Math.abs(liveLEyeLower.y - liveLEyeUpper.y)
  const liveRightEyeOpen = Math.abs(liveREyeLower.y - liveREyeUpper.y)

  if (!neutralPoints?.length) {
    const jawByRatio = clamp((liveMouthOpen / liveMouthWidth - 0.25) / 0.45, 0, 1)
    return {
      jawOpen: jawByRatio,
      blinkLeft: clamp((0.04 - liveLeftEyeOpen) / 0.03, 0, 1),
      blinkRight: clamp((0.04 - liveRightEyeOpen) / 0.03, 0, 1)
    }
  }

  const baseMouthUpper = pickCanonicalPoint(neutralPoints, LANDMARKS.mouthUpper)
  const baseMouthLower = pickCanonicalPoint(neutralPoints, LANDMARKS.mouthLower)
  const baseLEyeUpper = pickCanonicalPoint(neutralPoints, LANDMARKS.leftEyeUpper)
  const baseLEyeLower = pickCanonicalPoint(neutralPoints, LANDMARKS.leftEyeLower)
  const baseREyeUpper = pickCanonicalPoint(neutralPoints, LANDMARKS.rightEyeUpper)
  const baseREyeLower = pickCanonicalPoint(neutralPoints, LANDMARKS.rightEyeLower)
  if (
    !baseMouthUpper ||
    !baseMouthLower ||
    !baseLEyeUpper ||
    !baseLEyeLower ||
    !baseREyeUpper ||
    !baseREyeLower
  ) {
    return fallback
  }

  const baseMouthOpen = Math.max(1e-4, Math.abs(baseMouthLower.y - baseMouthUpper.y))
  const baseLeftEyeOpen = Math.max(1e-4, Math.abs(baseLEyeLower.y - baseLEyeUpper.y))
  const baseRightEyeOpen = Math.max(1e-4, Math.abs(baseREyeLower.y - baseREyeUpper.y))

  return {
    jawOpen: clamp((liveMouthOpen - baseMouthOpen * 0.9) / (baseMouthOpen * 1.15 + 0.016), 0, 1),
    blinkLeft: clamp((baseLeftEyeOpen - liveLeftEyeOpen) / (baseLeftEyeOpen * 0.85 + 0.01), 0, 1),
    blinkRight: clamp(
      (baseRightEyeOpen - liveRightEyeOpen) / (baseRightEyeOpen * 0.85 + 0.01),
      0,
      1
    )
  }
}

export default {
  createPointArray,
  computeFaceMetrics,
  toCanonical,
  canonicalToCanvas,
  applyRegionScale,
  applyRegionScaleAsymY,
  pickCanonicalPoint,
  getEyeCenter,
  avgY,
  translateIndicesY,
  translateIndicesWeighted,
  selectNearestIndices,
  readIrisCenter,
  computeEyeGaze,
  boostWeightsNear,
  buildExpressionWeights,
  buildFeatureWrapProfile,
  applyFeatureAwareWrap,
  decimateTriangles,
  stripMouthTriangles,
  computeFacialSignals,
  FACE_MESH_CONTOUR_PATHS
}
